import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useEffect } from 'react';
import { Home, Users, Award, Clock } from 'lucide-react';

const stats = [
  {
    icon: Home,
    value: 500,
    suffix: '+',
    label: 'Projects Completed',
    description: 'Homes transformed across Arkalgud',
  },
  {
    icon: Users,
    value: 1000,
    suffix: '+',
    label: 'Happy Clients',
    description: 'Satisfied homeowners',
  },
  {
    icon: Award,
    value: 15,
    suffix: '+',
    label: 'Years Experience',
    description: 'Of trusted service',
  },
  {
    icon: Clock,
    value: 100,
    suffix: '%',
    label: 'On-Time Delivery',
    description: 'Commitment to deadlines',
  },
];

const AnimatedCounter = ({ value, suffix, isInView }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));

  useEffect(() => {
    if (isInView) {
      const controls = animate(count, value, {
        duration: 2,
        ease: 'easeOut',
      });
      return controls.stop;
    }
  }, [isInView, value, count]);

  return (
    <motion.span className="counter-value">
      <motion.span>{rounded}</motion.span>
      {suffix}
    </motion.span>
  );
};

export const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="py-20 relative overflow-hidden" ref={ref}>
      {/* Background */}
      <div className="absolute inset-0 bg-secondary" />
      <div 
        className="absolute inset-0 parallax-bg opacity-10"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzfGVufDB8fHx8MTc2OTA5NDg5Nnww&ixlib=rb-4.1.0&q=85')`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/95 to-secondary" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="text-center"
            >
              {/* Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.15, type: 'spring' }}
                className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center"
              >
                <stat.icon className="w-8 h-8 text-primary-light" />
              </motion.div>

              {/* Counter */}
              <div className="mb-2">
                <AnimatedCounter 
                  value={stat.value} 
                  suffix={stat.suffix} 
                  isInView={isInView}
                />
              </div>

              {/* Label */}
              <h3 className="font-display text-lg font-semibold text-secondary-foreground mb-1">
                {stat.label}
              </h3>
              <p className="text-sm text-secondary-foreground/70">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
